import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STRINGS } from '../lib/i18n';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { apiUrl } from '../lib/api';
import { saveToken } from '../lib/session';
import { UNIVERSITIES } from '../lib/universities';

type Step = 1 | 2;

export function Onboarding() {
  const nav = useNavigate();
  const [lang, setLang] = useState<'ar' | 'en'>('ar');
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState('');
  const [pickedUniversity, setPickedUniversity] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionExpired = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('session_expired');
  const s = STRINGS.onboarding;

  useEffect(() => {
    fetch(apiUrl('/api/public/config')).then((r) => r.json()).then((j: { turnstile_site_key: string | null }) => {
      setTurnstileSiteKey(j.turnstile_site_key);
    }).catch(() => setTurnstileSiteKey(null));
  }, []);

  const isRtl = lang === 'ar';

  function nextStep(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) { setError(s.errors.name[lang]); return; }
    if (turnstileSiteKey && !turnstileToken) {
      setError(lang === 'ar' ? 'يرجى استكمال التحقق' : 'Please complete verification');
      return;
    }
    setStep(2);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!pickedUniversity) {
      setError(s.errors.prediction[lang]);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(apiUrl('/api/onboard'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          prediction_university: pickedUniversity,
          turnstileToken: turnstileToken || 'dev-bypass',
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json() as { token?: string };
      if (json.token) saveToken(json.token);
      nav('/chat');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <main className="min-h-full flex flex-col bg-tatweer-light">
      <header className="bg-tatweer-navy w-full">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-4">
            <img
              src="/brand/tatweer-white.svg"
              alt="Tatweer"
              className="h-12 w-auto"
            />
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">{s.badge[lang]}</div>
              <div className="text-lg font-bold text-white leading-tight mt-0.5">{s.title[lang]}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setLang((l) => (l === 'ar' ? 'en' : 'ar'))}
            className="text-xs text-white/80 hover:text-white px-4 py-2 rounded-full ring-1 ring-white/20 hover:ring-white/40 transition"
          >
            {lang === 'ar' ? 'EN' : 'عربي'}
          </button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="max-w-md w-full" dir={isRtl ? 'rtl' : 'ltr'}>
          {sessionExpired && (
            <div className="mb-4 rounded-2xl bg-amber-50 ring-1 ring-tatweer-gold/40 p-4 text-sm text-tatweer-body">
              <span className="font-bold text-tatweer-gold">⏱</span>{' '}
              {lang === 'ar'
                ? 'انتهت الجلسة السابقة. أدخل اسمك مرة أخرى للاستئناف.'
                : 'Your previous session expired. Re-enter your name to continue.'}
            </div>
          )}

          <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-tatweer-muted">
            <span className={step === 1 ? 'text-tatweer-navy font-semibold' : ''}>{lang === 'ar' ? '١. الاسم' : '1. Name'}</span>
            <span className="text-tatweer-line">·</span>
            <span className={step === 2 ? 'text-tatweer-navy font-semibold' : ''}>{lang === 'ar' ? '٢. توقّعك' : '2. Your guess'}</span>
          </div>

          <div className="bg-white rounded-2xl shadow-card-lg ring-1 ring-tatweer-line p-7 sm:p-9">
            {step === 1 ? (
              <form onSubmit={nextStep} className="space-y-6 animate-step-in">
                <label className="block">
                  <span className="text-xl sm:text-2xl font-bold text-tatweer-navy bidi-plaintext">
                    {s.nameLabel[lang]}
                  </span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={24}
                    autoFocus
                    placeholder={s.namePlaceholder[lang]}
                    className="mt-2 block w-full rounded-xl bg-white ring-1 ring-tatweer-line px-4 py-4 text-lg text-tatweer-body placeholder:text-tatweer-muted focus:outline-none focus:ring-2 focus:ring-tatweer-navy transition"
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </label>

                <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />

                {error && <div className="text-rose-600 text-sm">{error}</div>}

                <button
                  type="submit"
                  disabled={!name.trim()}
                  className="w-full rounded-xl bg-tatweer-navy hover:bg-tatweer-navy-700 active:bg-tatweer-navy-800 text-white font-semibold py-3.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {s.next[lang]}
                </button>

                <p className="text-[11px] text-tatweer-muted text-center pt-2 border-t border-tatweer-line">
                  {s.footer[lang]}
                </p>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-6 animate-step-in">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-tatweer-muted mb-1">
                    {s.step2Greeting[lang].replace('{name}', name.trim())}
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-tatweer-navy bidi-plaintext leading-tight">
                    {s.step2Title[lang]}
                  </h1>
                  <p className="mt-2 text-sm text-tatweer-body-soft leading-relaxed">
                    {s.step2Subtitle[lang]}
                  </p>
                </div>

                <label className="block">
                  <span className="text-xs font-medium uppercase tracking-wider text-tatweer-muted">
                    {s.predictionLabel[lang]}
                  </span>
                  <select
                    value={pickedUniversity}
                    onChange={(e) => setPickedUniversity(e.target.value)}
                    className="mt-2 block w-full rounded-xl bg-white ring-1 ring-tatweer-line px-4 py-4 text-lg text-tatweer-body focus:outline-none focus:ring-2 focus:ring-tatweer-navy transition"
                    dir={isRtl ? 'rtl' : 'ltr'}
                  >
                    <option value="" disabled>
                      {lang === 'ar' ? '— اختر جامعة —' : '— Pick a university —'}
                    </option>
                    {UNIVERSITIES.map((u) => (
                      <option key={u.key} value={u.key}>
                        {lang === 'ar' ? u.ar : u.en}
                      </option>
                    ))}
                  </select>
                </label>

                {error && <div className="text-rose-600 text-sm">{error}</div>}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 rounded-xl bg-white ring-1 ring-tatweer-line text-tatweer-body font-medium py-3.5 hover:bg-tatweer-light transition"
                  >
                    {s.back[lang]}
                  </button>
                  <button
                    type="submit"
                    disabled={!pickedUniversity || busy}
                    className="flex-[2] rounded-xl bg-tatweer-navy hover:bg-tatweer-navy-700 active:bg-tatweer-navy-800 text-white font-semibold py-3.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busy ? '…' : s.submit[lang]}
                  </button>
                </div>

                <p className="text-[11px] text-tatweer-muted text-center pt-2 border-t border-tatweer-line">
                  {s.footer[lang]}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
